import { ForwardRefExoticComponent, forwardRef, useState, Ref, RefAttributes } from 'react';
import styled from 'styled-components';

import { GalleryItemMobile } from 'productTemplate/components/imageGallery/GalleryItemMobile';
import { IExternalPicture } from 'shared/types/ExternalPicture';
import { MOBILE_BANNER_HEIGHT } from 'banner/enums/constants';
import { NAV_PDP_MOBILE_HEADER_HEIGHT } from 'layout/header/enums/constants';
import { ProgressIndicator } from 'shared/components/Carousel/ProgressIndicator';
import { sendTrackingEvent } from 'tracking/utils/sendTrackingEvent';
import { SwiperCarousel } from 'shared/components/Carousel/SwiperCarousel';
import { useIsBannerVisible } from 'shared/hooks/useIsBannerVisible';
import { useStoreSelector } from 'store/hooks/storeHooks';
import { variableFontRegular } from 'styles/theme/text';

interface IImageGalleryMobileStatic {
  Wrapper: any;
  ProgressIndicator: any;
}

interface IImageGalleryMobileProps {
  itemList: IExternalPicture[] | { mainPictureUrl: string }[];
}

type ImageGalleryMobileComponent = ForwardRefExoticComponent<
  IImageGalleryMobileProps & RefAttributes<HTMLDivElement>
> &
  IImageGalleryMobileStatic;

export const ImageGalleryMobile = forwardRef(
  ({ itemList }: IImageGalleryMobileProps, ref?: Ref<HTMLDivElement>) => {
    const { slug, robotAssets } = useStoreSelector((state) => state.pdp.productTemplate);
    const [activeIndex, setActiveIndex] = useState<number>(0);
    const isBannerVisible = useIsBannerVisible();

    const handleSlideChange = (swipercore) => {
      const { activeIndex: idx } = swipercore;
      const list = itemList as { mainPictureUrl: string }[];

      const robotsLength = robotAssets.length >= 3 ? 3 : robotAssets.length;

      if (list.length - robotsLength <= idx) {
        sendTrackingEvent('PDP_MODEL_CAROUSEL_VIEW', {
          product_template_slug: slug,
          picture_url: list[idx]?.mainPictureUrl,
        });
      } else {
        sendTrackingEvent('PDP_IMAGE_VIEW', {
          product_template_slug: slug,
          picture_id: list[idx]?.mainPictureUrl,
        });
      }

      setActiveIndex(idx);
    };

    return (
      <ImageGalleryMobile.Wrapper data-qa="image_gallery_mobile" $isBannerVisible={isBannerVisible}>
        <div style={{ position: 'relative' }}>
          <SwiperCarousel
            itemList={itemList}
            carouselItem={<GalleryItemMobile />}
            lazyLoad
            // swiper props below
            onSlideChange={handleSlideChange}
            centeredSlides
            slidesPerView={1}
            allowMissingAsset
          />
          {/* this is the observer for showing the thumbnail on mobile PDP */}
          {ref && <div ref={ref} className="mainImageObserver" />}
        </div>
        {itemList.length > 1 && (
          <ImageGalleryMobile.ProgressIndicator
            activeIndex={activeIndex + 1}
            itemListLength={itemList.length}
          />
        )}
      </ImageGalleryMobile.Wrapper>
    );
  },
) as ImageGalleryMobileComponent;

const NEW_MOBILE_HEADER_AND_BANNER_HEIGHT = `calc(${NAV_PDP_MOBILE_HEADER_HEIGHT} + ${MOBILE_BANNER_HEIGHT})`;

ImageGalleryMobile.Wrapper = styled.div<{
  $isCrawler: boolean;
  $isBannerVisible: boolean;
}>`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1 1 auto;
  justify-content: center;
  overflow: hidden;

  ${({ $isBannerVisible }) =>
    `
      margin-top: ${
        $isBannerVisible ? NEW_MOBILE_HEADER_AND_BANNER_HEIGHT : NAV_PDP_MOBILE_HEADER_HEIGHT
      };
  `}

  .swiper-wrapper {
    display: flex;
    align-items: center;
  }

  .swiper-container {
    width: 100vw;
    overflow: hidden;
  }

  .swiper-slide {
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
  }

  .mainImageObserver {
    position: absolute;
    width: 100vw;

    ${({ $isBannerVisible }) =>
      `
    bottom: ${
      $isBannerVisible
        ? `calc(${NAV_PDP_MOBILE_HEADER_HEIGHT} + ${MOBILE_BANNER_HEIGHT} + 56px)`
        : `calc(${NAV_PDP_MOBILE_HEADER_HEIGHT} + 56px)`
    }; /* 56px is from the top padding of GalleryItemMobile.Wrapper */
  `}
  }
`;

ImageGalleryMobile.ProgressIndicator = styled(ProgressIndicator)`
  ${variableFontRegular({ size: '8px', height: '8px' })};
`;
